import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Divider from "@material-ui/core/Divider";
import MemoryIcon from "@material-ui/icons/Memory";

import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(1),
  },
  balance: {
    fontWeight: 700,
  },
  topupRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  alert: {
    padding: theme.spacing(1),
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  blocked: {
    backgroundColor: "rgba(244, 67, 54, 0.12)",
  },
  warning: {
    backgroundColor: "rgba(255, 152, 0, 0.14)",
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
}));

const TOPUP_STATUS = {
  awaiting_payment: { labelKey: "aiCredit.topupStatus.awaitingPayment", color: "#9e9e9e" },
  pending: { labelKey: "aiCredit.topupStatus.pending", color: "#2196f3" },
  credited: { labelKey: "aiCredit.topupStatus.credited", color: "#4caf50" },
  failed: { labelKey: "aiCredit.topupStatus.failed", color: "#ff9800" },
  cancelled: { labelKey: "aiCredit.topupStatus.cancelled", color: "#9e9e9e" },
  reversed: { labelKey: "aiCredit.topupStatus.reversed", color: "#795548" },
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

// Card de créditos de IA. Só aparece para empresas com a cobrança habilitada pelo
// dono do ambiente — quem está em cortesia (a casa paga) não vê nada.
export default function AiCreditCard({ onPay, refreshKey }) {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);

  const [status, setStatus] = useState(null);
  const [customValue, setCustomValue] = useState("");
  const [creating, setCreating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [topupRows, setTopupRows] = useState([]);
  const [usageRows, setUsageRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-credits/status");
      setStatus(data);
    } catch (err) {
      // silencioso: card some quando o status não pode ser lido
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, refreshKey]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);

    const onAiCredit = (data) => {
      if (data.action === "balance" || data.action === "lowBalance") {
        setStatus((prev) => {
          if (!prev) return prev;
          // Mesma regra do backend: sem recalcular aqui, o aviso de saldo baixo só
          // apareceria (ou sumiria, depois da recarga) no próximo reload.
          const threshold = Number(prev.lowBalanceAlert || 0);
          return {
            ...prev,
            balance: data.balance,
            blocked: data.blocked === undefined ? prev.blocked : data.blocked,
            lowBalance: threshold > 0 && Number(data.balance) <= threshold,
            stale: false,
          };
        });
      }
    };

    socket.on(`company-${companyId}-aiCredit`, onAiCredit);
    return () => {
      socket.off(`company-${companyId}-aiCredit`, onAiCredit);
    };
  }, [socketManager]);

  const handleTopup = async (creditValue) => {
    setCreating(true);
    try {
      const { data: invoice } = await api.post("/ai-credits/topup", {
        creditValue,
      });
      if (onPay) onPay(invoice);
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) {
      setHistoryLoading(true);
      try {
        const [topupsRes, usageRes] = await Promise.all([
          api.get("/ai-credits/topups"),
          api.get("/ai-credits/usage"),
        ]);
        setTopupRows(topupsRes.data.topups || []);
        setUsageRows(usageRes.data.items || []);
      } catch (err) {
        toastError(err);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  if (!status?.enabled) return null;

  // Recarga 1:1: o cliente paga o crédito que escolhe. A margem é descontada no
  // iaHub a cada chamada e não aparece nesta tela.
  const customCredit = Number(customValue);

  // Consumo por dia: o extrato detalhado (cada chamada de LLM) fica com o dono do
  // ambiente — numa conversa curta são dezenas de lançamentos e o cliente não tem
  // como interpretá-los.
  const usageByDay = usageRows.reduce((acc, item) => {
    const day = moment(item.day).format("DD/MM/YY");
    acc[day] = acc[day] || { day, calls: 0, chargedBRL: 0 };
    acc[day].calls += Number(item.calls || 0);
    acc[day].chargedBRL += Number(item.chargedBRL || 0);
    return acc;
  }, {});
  const usageDays = Object.values(usageByDay);

  return (
    <Paper className={classes.card} variant="outlined">
      <Box className={classes.header}>
        <MemoryIcon color="primary" />
        <Typography variant="subtitle1">{i18n.t("aiCredit.title")}</Typography>
      </Box>

      <Typography variant="h5" className={classes.balance}>
        {/* O hub debita em frações de centavo: -0,0025 sairia como "-R$ 0,00". */}
        {formatCurrency(Math.abs(Number(status.balance)) < 0.005 ? 0 : status.balance)}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        {status.currentMonth
          ? i18n.t("aiCredit.monthUsage", {
              calls: status.currentMonth.calls,
              value: formatCurrency(status.currentMonth.chargedBRL),
            })
          : i18n.t("aiCredit.consumptionInfo")}
        {status.stale && ` ${i18n.t("aiCredit.stale")}`}
      </Typography>

      {status.blocked && (
        <Box className={`${classes.alert} ${classes.blocked}`}>
          <Typography variant="body2">
            <strong>{i18n.t("aiCredit.blockedTitle")}</strong>{" "}
            {i18n.t("aiCredit.blockedHint")}
          </Typography>
        </Box>
      )}

      {!status.blocked && status.lowBalance && (
        <Box className={`${classes.alert} ${classes.warning}`}>
          <Typography variant="body2">
            {i18n.t("aiCredit.lowBalance")}
          </Typography>
        </Box>
      )}

      <Box className={classes.topupRow}>
        {(status.presets || []).map((preset) => (
          <Button
            key={preset.credit}
            size="small"
            variant="outlined"
            color="primary"
            disabled={creating}
            onClick={() => handleTopup(preset.credit)}
          >
            {formatCurrency(preset.credit)}
          </Button>
        ))}
        <TextField
          size="small"
          variant="outlined"
          type="number"
          label={i18n.t("aiCredit.otherCreditValue")}
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          inputProps={{ min: status.minCreditValue || 1, step: 1 }}
          style={{ width: 180 }}
        />
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={creating || !(customCredit > 0)}
          onClick={() => handleTopup(customCredit)}
        >
          {creating ? i18n.t("aiCredit.generating") : i18n.t("aiCredit.buy")}
        </Button>
        <Button size="small" onClick={toggleHistory}>
          {historyOpen
            ? i18n.t("aiCredit.hideUsage")
            : i18n.t("aiCredit.showUsage")}
        </Button>
      </Box>

      <Collapse in={historyOpen}>
        <Box mt={2} className={classes.tableWrapper}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("aiCredit.usageByDay")}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("aiCredit.table.day")}</TableCell>
                    <TableCell align="right">
                      {i18n.t("aiCredit.table.interactions")}
                    </TableCell>
                    <TableCell align="right">
                      {i18n.t("aiCredit.table.consumption")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageDays.map((row) => (
                    <TableRow key={row.day}>
                      <TableCell>{row.day}</TableCell>
                      <TableCell align="right">{row.calls}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.chargedBRL)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {usageDays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {i18n.t("aiCredit.noUsage")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Divider style={{ margin: "16px 0" }} />

              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("aiCredit.topups")}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("common.date")}</TableCell>
                    <TableCell>{i18n.t("aiCredit.table.status")}</TableCell>
                    <TableCell align="right">
                      {i18n.t("aiCredit.table.credit")}
                    </TableCell>
                    <TableCell align="right">
                      {i18n.t("aiCredit.table.paid")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topupRows.map((topup) => {
                    const statusInfo = TOPUP_STATUS[topup.hubStatus];
                    const baseInfo = statusInfo
                      ? { ...statusInfo, label: i18n.t(statusInfo.labelKey) }
                      : {
                          label: topup.hubStatus,
                          color: "#9e9e9e",
                        };
                    // Reembolso parcial: a recarga segue creditada, menos o estornado.
                    const info =
                      topup.hubStatus === "credited" &&
                      Number(topup.hubReversedAmount) > 0
                        ? {
                            ...baseInfo,
                            label: i18n.t("aiCredit.topupStatus.withReversal", {
                              label: baseInfo.label,
                              value: formatCurrency(topup.hubReversedAmount),
                            }),
                          }
                        : baseInfo;
                    return (
                      <TableRow key={topup.id}>
                        <TableCell>
                          {moment(topup.createdAt).format("DD/MM/YY HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={info.label}
                            style={{
                              backgroundColor: info.color,
                              color: "#fff",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(topup.creditAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(topup.amountPaid)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {topupRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {i18n.t("aiCredit.noTopups")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
